import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    AppBar,
    Toolbar,
    Box,
    IconButton,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { categoriasForNavigationBar } from '../data/categorias-prisma';
import DesktopMenu from './navigation/DesktopMenu';
import MobileMenu from './navigation/MobileMenu';
import LanguageSwitcher from './navigation/LanguageSwitcher';
import CustomImageComponent from '@/components/shared/custom/custom-image';

const PrismaNavigationBar = ({ currentCategory, onNavigateHome, onNavigateCategory }) => {
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const [activeCategory, setActiveCategory] = useState(currentCategory ?? null);

    useEffect(() => {
        setActiveCategory(currentCategory ?? null);
    }, [currentCategory]);

    const categories = useMemo(() => categoriasForNavigationBar() ?? [], []);

    const toggleMobileMenu = useCallback(() => {
        setMobileDrawerOpen((prev) => !prev);
    }, []);

    const closeMobileMenu = useCallback(() => {
        setMobileDrawerOpen(false);
    }, []);

    const handleNavigateHome = useCallback(() => {
        setActiveCategory(null);
        onNavigateHome?.();
        closeMobileMenu();
    }, [onNavigateHome, closeMobileMenu]);

    const handleNavigateCategory = useCallback(
        (categoryId) => {
            setActiveCategory(categoryId);
            onNavigateCategory?.(categoryId);
            closeMobileMenu();
        },
        [onNavigateCategory, closeMobileMenu]
    );

    return (
        <>
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    backdropFilter: 'saturate(180%) blur(8px)',
                    backgroundImage: 'none',
                }}
            >
                <Box className="container">
                    <Toolbar
                        sx={{
                            minHeight: { xs: 56, sm: 64 },
                            px: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >

                        <Box
                            onClick={handleNavigateHome}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                cursor: 'pointer',
                                userSelect: 'none',
                                '&:hover': { opacity: 0.8 },
                            }}
                        >
                            <CustomImageComponent
                                className="image-logo__image"
                                src={`${process.env.NEXT_PUBLIC_IMAGES}/prisma/logo-transparent-svg.svg`}
                                alt="logo"
                                width={100}
                                height={60}
                                priority={true}
                            />
                        </Box>

                        <Box sx={{ flex: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
                            <DesktopMenu
                                currentCategory={activeCategory}
                                categories={categories}
                                onNavigateCategory={handleNavigateCategory}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                            <LanguageSwitcher />
                            <IconButton
                                aria-label="Abrir menú"
                                edge="end"
                                onClick={toggleMobileMenu}
                                sx={{
                                    display: { xs: 'inline-flex', md: 'none' },
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: mobileDrawerOpen ? 'primary.main' : 'divider',
                                    mr: 1,
                                }}
                            >
                                <MenuIcon />
                            </IconButton>
                        </Box>
                    </Toolbar>
                </Box>
            </AppBar>

            <MobileMenu
                currentCategory={activeCategory}
                onNavigateHome={handleNavigateHome}
                onNavigateCategory={handleNavigateCategory}
                categories={categories}
                isOpen={mobileDrawerOpen}
                onClose={closeMobileMenu}
            />
        </>
    );
};

export default PrismaNavigationBar;
